import axios from 'axios';
import type {
  PublicationStatsDto,
  PublicationListDto,
  PublicationDetailDto,
  MentionDetailDto,
  MonitoringUserDto,
  PublicationListQuery,
  PagedResult,
  ParsingHistoryResponseDto,
  PublicationRetryRequest,
  PublicationRetryResponse,
} from '../types/publication';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

/**
 * Admin API client for making authenticated requests to admin endpoints.
 * All requests require admin privileges.
 */

/**
 * Health check endpoint to verify admin API is operational.
 * Requires admin authentication.
 * 
 * @param token - JWT authentication token
 * @returns Health status response
 */
export const checkAdminHealth = async (token: string) => {
  const response = await axios.get(`${API_BASE_URL}/admin/health`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * Get publication statistics.
 * Returns aggregate statistics including total count and oldest publication.
 * 
 * @param token - JWT authentication token
 * @returns Publication statistics
 */
export const getPublicationStats = async (token: string): Promise<PublicationStatsDto> => {
  const response = await axios.get<PublicationStatsDto>(`${API_BASE_URL}/admin/publications/stats`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * Get paginated list of publications with filtering and sorting.
 * 
 * @param query - Query parameters for filtering, sorting, and pagination
 * @param token - JWT authentication token
 * @returns Paginated list of publications
 */
export const getPublications = async (
  query: PublicationListQuery,
  token: string
): Promise<PagedResult<PublicationListDto>> => {
  const params = new URLSearchParams();
  
  if (query.page !== undefined) params.append('page', query.page.toString());
  if (query.pageSize !== undefined) params.append('pageSize', query.pageSize.toString());
  if (query.sortBy) params.append('sortBy', query.sortBy);
  if (query.sortDescending !== undefined) params.append('sortDescending', query.sortDescending.toString());
  if (query.searchTerm) params.append('searchTerm', query.searchTerm);
  if (query.source) params.append('source', query.source);
  if (query.dateFrom) params.append('dateFrom', query.dateFrom);
  if (query.dateTo) params.append('dateTo', query.dateTo);

  const response = await axios.get<PagedResult<PublicationListDto>>(
    `${API_BASE_URL}/admin/publications?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

/**
 * Get detailed information about a specific publication.
 * 
 * @param publicationId - The unique identifier of the publication
 * @param token - JWT authentication token
 * @returns Publication details with mention count
 */
export const getPublicationById = async (
  publicationId: number,
  token: string
): Promise<PublicationDetailDto> => {
  const response = await axios.get<PublicationDetailDto>(
    `${API_BASE_URL}/admin/publications/${publicationId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

/**
 * Get paginated list of mentions for a specific publication.
 * 
 * @param publicationId - The unique identifier of the publication
 * @param page - Page number (1-based)
 * @param pageSize - Number of items per page
 * @param token - JWT authentication token
 * @returns Paginated list of mentions with national ID details
 */
export const getPublicationMentions = async (
  publicationId: number,
  page: number,
  pageSize: number,
  token: string
): Promise<PagedResult<MentionDetailDto>> => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('pageSize', pageSize.toString());

  const response = await axios.get<PagedResult<MentionDetailDto>>(
    `${API_BASE_URL}/admin/publications/${publicationId}/mentions?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

/**
 * Get users monitoring a specific national ID.
 * 
 * @param nationalIdId - The unique identifier of the national ID
 * @param token - JWT authentication token
 * @returns List of users monitoring this national ID
 */
export const getMonitoringUsers = async (
  nationalIdId: string,
  token: string
): Promise<MonitoringUserDto[]> => {
  const response = await axios.get<MonitoringUserDto[]>(
    `${API_BASE_URL}/admin/mentions/${nationalIdId}/monitors`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

/**
 * Get parsing history for a specific publication.
 * Returns complete parsing history with aggregate metrics showing
 * improvement over time.
 * 
 * @param publicationId - The unique identifier of the publication
 * @param token - JWT authentication token
 * @returns Parsing history with timeline and metrics
 */
export const getParsingHistory = async (
  publicationId: number,
  token: string
): Promise<ParsingHistoryResponseDto> => {
  const response = await axios.get<ParsingHistoryResponseDto>(
    `${API_BASE_URL}/admin/publications/${publicationId}/parsing-history`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

export const getPublicationParsingHistory = async (
  publicationId: number,
  token: string
): Promise<ParsingHistoryResponseDto> => {
  return getParsingHistory(publicationId, token);
};

export const retryPublication = async (
  publicationId: number,
  request: PublicationRetryRequest,
  token: string
): Promise<PublicationRetryResponse> => {
  const response = await axios.post<PublicationRetryResponse>(
    `${API_BASE_URL}/admin/publications/${publicationId}/retry`,
    request,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return response.data;
};

/**
 * Trigger publication ingestion manually.
 * Admin-only endpoint to run the publication ingestion cycle.
 * 
 * @param token - JWT authentication token
 * @returns Success message with timestamp
 */
export const triggerPublicationIngestion = async (
  token: string
): Promise<{ message: string; timestamp: string }> => {
  const response = await axios.post<{ message: string; timestamp: string }>(
    `${API_BASE_URL}/admin/publications/trigger-ingestion`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};
